import type { StoreProjectApiStatus } from './store-project-fields.js'

const READ = ['read'] as const
const READ_EDIT = ['read', 'edit'] as const
export type RbacPermission = (typeof READ)[number] | (typeof READ_EDIT)[number]
export const rbacPermissions = [...new Set([...READ, ...READ_EDIT])] as const

const GLOBAL = ['global'] as const
const REAL_ESTATE_RESTRICTED = ['real-estate-restricted'] as const
const GLOBAL_STORE = ['global', 'store'] as const
export type RbacScope =
  | (typeof GLOBAL)[number]
  | (typeof GLOBAL_STORE)[number]
  | (typeof REAL_ESTATE_RESTRICTED)[number]
export const rbacScopes = [
  ...new Set([...GLOBAL, ...GLOBAL_STORE, ...REAL_ESTATE_RESTRICTED]),
] as const

interface RbacConditionFieldTypes {
  status: StoreProjectApiStatus
}

type RbacResourceDetailsInterface = Record<
  string,
  {
    actions: readonly RbacPermission[]
    scopes: readonly RbacScope[]
    conditionField?: keyof RbacConditionFieldTypes
  }
>

export const rbacResourceDetails = {
  dashboard: { actions: READ, scopes: GLOBAL },
  today: { actions: READ, scopes: GLOBAL },
  carts: { actions: READ, scopes: GLOBAL },
  categories: { actions: READ_EDIT, scopes: GLOBAL },
  products: { actions: READ_EDIT, scopes: GLOBAL },
  prices: { actions: READ_EDIT, scopes: GLOBAL },
  users: { actions: READ_EDIT, scopes: GLOBAL },
  promos: { actions: READ_EDIT, scopes: GLOBAL },
  'work-concil': { actions: READ_EDIT, scopes: GLOBAL },
  notifications: { actions: READ_EDIT, scopes: GLOBAL },
  stores: { actions: READ_EDIT, scopes: GLOBAL },
  accounting: { actions: READ_EDIT, scopes: GLOBAL },
  quality: { actions: READ_EDIT, scopes: GLOBAL },
  'cart-control': { actions: READ_EDIT, scopes: GLOBAL },
  'daily-sales': { actions: READ_EDIT, scopes: GLOBAL },
  sellers: { actions: READ_EDIT, scopes: GLOBAL },
  cameras: { actions: READ_EDIT, scopes: GLOBAL },
  'camera-views': { actions: READ_EDIT, scopes: GLOBAL },
  sensors: { actions: READ_EDIT, scopes: GLOBAL },
  'hr-planning': { actions: READ_EDIT, scopes: GLOBAL },
  'real-estate': {
    actions: READ_EDIT,
    scopes: [...GLOBAL, ...REAL_ESTATE_RESTRICTED],
    conditionField: 'status' as const,
  },
  roles: { actions: READ_EDIT, scopes: GLOBAL },
  'platform-stocks': { actions: READ_EDIT, scopes: GLOBAL },
  'visio-reviews': { actions: READ_EDIT, scopes: GLOBAL },
} as const satisfies RbacResourceDetailsInterface

export type RbacResourceDetails = typeof rbacResourceDetails

export type ReadableRbacResource = {
  [K in RbacResource]: 'read' extends RbacResourceDetails[K]['actions'][number] ? K : never
}[RbacResource]

export const rbacResources = Object.keys(rbacResourceDetails) as (keyof RbacResourceDetails)[]
export type RbacResource = (typeof rbacResources)[number]

/**
 * Union of [action, resource] tuples derived from rbacResourceDetails.
 * Use this to type MongoAbility — TypeScript then enforces per-resource action constraints,
 * e.g. ability.can('edit', 'dashboard') is a compile error since dashboard only allows 'read'.
 *
 * For resources with a conditionField (e.g. 'real-estate' → 'status'), the subject can also
 * be an object carrying that field so CASL evaluates MongoDB-style conditions correctly.
 */
export type ResourceAbilityTuple = {
  [K in RbacResource]: (typeof rbacResourceDetails)[K] extends {
    conditionField: infer F extends keyof RbacConditionFieldTypes
  }
    ? [
        (typeof rbacResourceDetails)[K]['actions'][number],
        K | { [P in F]: RbacConditionFieldTypes[P] },
      ]
    : [(typeof rbacResourceDetails)[K]['actions'][number], K]
}[RbacResource]

export type PermissionKey = {
  [K in keyof RbacResourceDetails]: `${K}:${RbacResourceDetails[K]['actions'][number]}`
}[keyof RbacResourceDetails]

export type ParsedPermissionKey<K extends PermissionKey = PermissionKey> =
  K extends `${infer R}:${infer P}`
    ? R extends RbacResource
      ? P extends RbacPermission
        ? { resource: R; permissionType: P }
        : never
      : never
    : never

export interface RbacRolePermission {
  resource: RbacResource
  permissionType: RbacPermission
  scope: RbacScope
}

export interface CurrentAdminRbacPermissionGrant extends RbacRolePermission {
  permissionKey: PermissionKey
}

export interface CurrentAdminRbacPermissionsResponse {
  enforcementEnabled: boolean
  grants: CurrentAdminRbacPermissionGrant[]
}

export interface RbacRoleAdmin {
  id: number
  email: string
  firstName: string
  lastName: string
}

export interface RbacRole {
  id: number
  name: string
  description: string | null
  permissions: RbacRolePermission[]
  admins: RbacRoleAdmin[]
}

export interface CreateRbacRoleRequest {
  name: string
  description?: string | null
  permissions?: RbacRolePermission[]
  adminIds?: number[]
}

export interface UpdateRbacRoleRequest {
  name?: string
  description?: string | null
}

export interface ReplaceRbacRolePermissionsRequest {
  permissions: RbacRolePermission[]
}

export interface ReplaceRbacRoleAdminsRequest {
  adminIds: number[]
}

/**
 * Row returned by GET /feature-flags (Lucid JSON: camelCase, ISO date strings).
 * Aligned with `TimestampsAndID` without importing `index.ts` (avoids circular export).
 */
export interface FeatureFlag {
  id: number
  createdAt: string
  updatedAt: string
  name: string
  enabled: boolean
}

/** `feature_flags.name` value; backend RBAC enforcement reads this via FeatureFlagService. */
export const RBAC_ENFORCEMENT_FEATURE_FLAG = 'ENFORCE_RBAC_SYSTEM' as const
